/**
 * Wire → domain normalisation.
 *
 * The server is being written in parallel, so the UI stays tolerant about the
 * exact casing it receives (`note_id` vs `noteId`, `updated_at` vs `updatedAt`)
 * and about optional fields being absent. Everything downstream of this module
 * sees the clean shapes from types.ts.
 */

import type { Attachment, AttachmentList, FolderCount, HealthStatus, Note, NoteSummary, SearchHit, SearchResponse, TagCount } from "./types";

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function pick(source: Record<string, unknown>, ...keys: readonly string[]): unknown {
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return undefined;
}

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function asStringArray(value: unknown): readonly string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((entry): entry is string => typeof entry === "string");
}

function asArray(value: unknown): readonly unknown[] {
  return Array.isArray(value) ? value : [];
}

export function decodeNoteSummary(value: unknown): NoteSummary {
  const record = asRecord(value);
  return {
    noteId: asString(pick(record, "noteId", "note_id", "id")),
    title: asString(pick(record, "title"), "(untitled)"),
    folder: asString(pick(record, "folder"), "inbox"),
    tags: asStringArray(pick(record, "tags")),
    // vineelernote-server serves these as `created`/`updated`.
    createdAt: asString(pick(record, "createdAt", "created_at", "created")),
    updatedAt: asString(pick(record, "updatedAt", "updated_at", "updated")),
  };
}

export function decodeNote(value: unknown): Note {
  const record = asRecord(value);
  const summary = decodeNoteSummary(record);
  return {
    ...summary,
    body: asString(pick(record, "body", "content")),
    keywords: asStringArray(pick(record, "keywords")),
    origin: asString(pick(record, "origin")),
  };
}

export function decodeNotes(value: unknown): readonly NoteSummary[] {
  const record = asRecord(value);
  // `/api/notes/recent` returns the same `{total, hits}` envelope as search.
  const list = Array.isArray(value) ? value : asArray(pick(record, "hits", "notes", "items", "results"));
  return list.map(decodeNoteSummary);
}

/**
 * Snippets arrive as the server's Bleve-shaped fragment map — field name to a
 * list of `<mark>`-wrapped `ts_headline` fragments, e.g.
 * `{"body": ["… <mark>postgres</mark> …"], "title": ["<mark>Postgres</mark> notes"]}`.
 * A field is present only when it actually matched. Body reads better as a
 * snippet than the title, which is already displayed in full above it.
 */
function decodeSnippet(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === "string").join(" … ");
  }
  if (value !== null && typeof value === "object") {
    const record = value as Record<string, unknown>;
    const preferred = pick(record, "body", "title");
    if (preferred !== undefined) {
      return decodeSnippet(preferred);
    }
    const first = Object.values(record)[0];
    return first === undefined ? "" : decodeSnippet(first);
  }
  return "";
}

function decodeSearchHit(value: unknown): SearchHit {
  const record = asRecord(value);
  // A hit may either wrap the note (`{ note: {...}, snippet }`) or be a note
  // with the snippet fields mixed in at the top level. vineelernote-server does
  // the latter, and calls the snippet field `fragment`.
  const noteSource = pick(record, "note") ?? record;
  return {
    note: decodeNoteSummary(noteSource),
    snippet: decodeSnippet(pick(record, "snippet", "highlight", "headline", "fragment")),
    score: asNumber(pick(record, "score", "rank")),
  };
}

export function decodeSearchResponse(value: unknown): SearchResponse {
  const record = asRecord(value);
  const rawHits = Array.isArray(value) ? value : asArray(pick(record, "hits", "results", "notes"));
  const hits = rawHits.map(decodeSearchHit);
  const total = asNumber(pick(record, "total", "count"), hits.length);
  return { hits, total };
}

export function decodeTagCounts(value: unknown): readonly TagCount[] {
  const record = asRecord(value);
  const raw = pick(record, "tags", "items") ?? value;

  // Accept `["a","b"]`, `{"a": 3}` and `[{tag, count}]`.
  if (Array.isArray(raw)) {
    return raw.map((entry): TagCount => {
      if (typeof entry === "string") {
        return { tag: entry, count: 0 };
      }
      const tagRecord = asRecord(entry);
      return {
        tag: asString(pick(tagRecord, "tag", "name", "value")),
        count: asNumber(pick(tagRecord, "count", "total")),
      };
    });
  }

  if (raw !== null && typeof raw === "object") {
    return Object.entries(raw as Record<string, unknown>).map(
      ([tag, count]): TagCount => ({ tag, count: asNumber(count) }),
    );
  }

  return [];
}

export function decodeFolderCounts(value: unknown): readonly FolderCount[] {
  const record = asRecord(value);
  const raw = Array.isArray(value) ? value : asArray(pick(record, "folders", "items"));
  // Accept the old bare `["a","b"]` shape as well as `[{folder, count}]`.
  return raw
    .map(
      (entry): FolderCount =>
        typeof entry === "string"
          ? { folder: entry, count: 0 }
          : {
              folder: asString(pick(asRecord(entry), "folder", "name", "path")),
              count: asNumber(pick(asRecord(entry), "count", "total")),
            },
    )
    .filter((entry) => entry.folder !== "");
}

/** Folder names only, for the note editor's picker. */
export function decodeFolders(value: unknown): readonly string[] {
  return decodeFolderCounts(value).map((entry) => entry.folder);
}

export function decodeHealth(value: unknown): HealthStatus {
  const record = asRecord(value);
  const status: HealthStatus = { status: asString(pick(record, "status", "state"), "unknown") };
  const noteCount = pick(record, "noteCount", "note_count", "notes");
  const version = pick(record, "version");
  return {
    ...status,
    ...(typeof noteCount === "number" ? { noteCount } : {}),
    ...(typeof version === "string" ? { version } : {}),
  };
}

export function decodeAttachment(value: unknown): Attachment {
  const record = asRecord(value);
  return {
    id: asString(pick(record, "id")),
    filename: asString(pick(record, "filename"), "file"),
    contentType: asString(pick(record, "contentType"), "application/octet-stream"),
    size: asNumber(pick(record, "size")),
    createdAt: asString(pick(record, "created")),
    previewable: pick(record, "previewable") === true,
    isImage: pick(record, "isImage") === true,
    previewUrl: asString(pick(record, "previewUrl")),
    downloadUrl: asString(pick(record, "downloadUrl")),
  };
}

export function decodeAttachmentList(value: unknown): AttachmentList {
  const record = asRecord(value);
  return {
    attachments: asArray(pick(record, "attachments")).map(decodeAttachment),
    enabled: pick(record, "enabled") === true,
    maxBytes: asNumber(pick(record, "maxBytes")),
  };
}
